import React from 'react'
import styled from 'styled-components'
import { Card, Progress, Image, useMatchBreakpoints } from '@nominex/dex-uikit'
import { SlSkeleton } from '@shoelace-style/shoelace/dist/react'
import { useTranslation } from 'contexts/Localization'
import useTheme from 'hooks/useTheme'
import { CashbackPoolResponse } from 'restApi/openapi/models'
import { formatNumber } from 'utils/formatters'

const StyledTitle = styled.h5`
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  color: ${({ theme }) => theme.colors.white};
`

const StyledSubtitle = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.white};
  opacity: 0.5;
`

const CashbackPoolWidget = ({ cashbackPool }: { cashbackPool: CashbackPoolResponse }) => {
  const { theme } = useTheme()
  const { t } = useTranslation()
  const { isMobile } = useMatchBreakpoints()

  const isDataReady = cashbackPool.total > 0
  const isPoolDistributed = isDataReady && cashbackPool.distributed >= cashbackPool.total

  const progress = (cashbackPool.distributed / cashbackPool.total) * 100

  const progressColor = isPoolDistributed ? theme.colors.red500 : theme.colors.purple500
  const progressLabel = isDataReady
    ? `${t('Distributed')} ${formatNumber(cashbackPool.distributed, {
        maximumFractionDigits: 1,
      })} / ${formatNumber(cashbackPool.total, {
        maximumFractionDigits: 1,
      })}`
    : t('Loading...')

  return (
    <Card
      background="linear-gradient(108.82deg, rgba(165, 0, 243, 0.2) 33.45%, rgba(2, 23, 216, 0.2) 99.53%);"
      withoutBorder
    >
      <div className="flex items-center">
        {!isMobile && <Image src="/images/banners/cashback-pool.png" height={112} width={112} />}

        <div className="p-12 w-[100%]">
          <StyledTitle>{t('Cashback pool')}</StyledTitle>

          {isDataReady ? (
            <StyledSubtitle>
              {t(
                'The total daily pool for cashback rewards is limited by %poolSize% NMX. The pool is refilled every day at 00:00:00 UTC.',
                {
                  poolSize: formatNumber(cashbackPool.total),
                },
              )}
            </StyledSubtitle>
          ) : (
            <>
              <SlSkeleton className="w-[100%] mt-8" effect="sheen" />
              <SlSkeleton className="w-[75%] mt-8" effect="sheen" />
            </>
          )}

          <div className="mt-12">
            <Progress
              label={progressLabel}
              color={progressColor}
              variant="semi-flat"
              primaryStep={progress}
              loading={!isDataReady}
            />
          </div>
        </div>
      </div>
    </Card>
  )
}

export default CashbackPoolWidget
